/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

/**
 * BackgroundVideo renders a companion's looping video background behind all
 * content on desktop-companion surfaces (Antechamber / Room).
 *
 * It sits at `z-index: -1` so it covers the wrapper's own gradient but stays
 * below every positioned content layer (avatar, subtitles, toolbar). The static
 * background image doubles as the `poster` (shown until the first frame decodes)
 * and as the graceful fallback if the clip fails to load. Callers gate mounting
 * on desktop-only + a non-empty `src`, so mobile never mounts a video.
 *
 * Looping: we use the browser's native forward `loop`. A truly seamless loop
 * requires the clip's first and last frames to match; for arbitrary uploads
 * that don't, the fix belongs at the source (make the clip loop-friendly),
 * because runtime tricks each trade one artifact for a worse one — crossfading
 * ghosts moving content (doubled doors/flames) and boomerang reverses
 * directional motion (flames falling) with a turnaround stutter.
 *
 * Sound: uploaded clips may carry music (the server preserves and loops the
 * audio track). Because browsers block *unmuted* autoplay, the element always
 * starts muted so the picture never fails to play; when `sound` is on we unmute
 * as soon as playback is allowed, and otherwise on the first user gesture.
 */

import { useEffect, useRef, useState } from 'react';

// Matches the light wash the static wrapper background used
// (`rgba(247,245,255,0.34)`), so foreground text keeps the same contrast it had
// before the video layer existed.
const DEFAULT_OVERLAY = 'rgba(247, 245, 255, 0.34)';

export interface BackgroundVideoProps {
  /** Looping video URL (mp4 / webm). */
  src: string;
  /** Static image shown as poster / fallback while the video is unavailable. */
  poster?: string;
  /**
   * Optional scrim painted above the video (below content) to keep foreground
   * text legible over busy footage. Defaults to the light wash the static
   * background used, so readability stays consistent whether the clip plays or
   * the poster shows.
   */
  overlay?: string;
  /**
   * When true, play the clip's audio track (if any). Defaults to false so the
   * background stays silent unless the caller opts in.
   */
  sound?: boolean;
}

function BackgroundVideo({
  src,
  poster,
  overlay = DEFAULT_OVERLAY,
  sound = false,
}: BackgroundVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Fall back to the poster image layer if the clip can't play (decode error,
  // network, unsupported codec) so the background never goes blank.
  const [failed, setFailed] = useState(false);
  // Fade the video in once its first frame is actually painting, so the
  // poster → video hand-off is a soft cross-fade instead of a visible pop.
  const [ready, setReady] = useState(false);

  // Always (re)start muted — unmuted autoplay is blocked — then apply the sound
  // preference once playback is under way.
  useEffect(() => {
    setFailed(false);
    setReady(false);
    const video = videoRef.current;
    if (!video) return undefined;
    const start = () => {
      const p = video.play();
      if (p) p.catch(() => {});
    };
    // `playing` fires once frames are actually being presented — the right
    // moment to reveal the video over the poster.
    const reveal = () => setReady(true);
    video.addEventListener('loadeddata', start);
    video.addEventListener('playing', reveal);
    if (video.readyState >= 2) start();
    return () => {
      video.removeEventListener('loadeddata', start);
      video.removeEventListener('playing', reveal);
      video.pause();
    };
  }, [src]);

  // Reflect the sound toggle. Unmuting may be rejected while the page has had no
  // user interaction, so if the immediate attempt doesn't stick we retry on the
  // next pointer/key/touch gesture (which browsers accept as activation).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    if (!sound) {
      video.muted = true;
      return undefined;
    }

    let cleanupGesture: (() => void) | undefined;
    const tryUnmute = () => {
      video.muted = false;
      video.volume = 1;
      const p = video.play();
      if (p) p.catch(() => {});
    };
    tryUnmute();

    // If the browser silently kept it muted (no user activation yet), arm a
    // one-shot gesture listener to unmute on the first interaction. An
    // AbortController removes all listeners at once, so the handler needn't
    // reference its own teardown.
    if (video.muted) {
      const controller = new AbortController();
      const onGesture = () => {
        tryUnmute();
        if (!video.muted) controller.abort();
      };
      const opts = { signal: controller.signal } as AddEventListenerOptions;
      window.addEventListener('pointerdown', onGesture, opts);
      window.addEventListener('keydown', onGesture, opts);
      window.addEventListener('touchstart', onGesture, opts);
      cleanupGesture = () => controller.abort();
    }

    return () => {
      cleanupGesture?.();
    };
  }, [sound, src, failed]);

  return (
    <>
      {/* Poster/fallback layer: also visible behind a video with transparency. */}
      {poster ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            backgroundImage: `url(${poster})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      ) : null}
      {!failed ? (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          draggable={false}
          onError={() => setFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -1,
            pointerEvents: 'none',
            userSelect: 'none',
            opacity: ready ? 1 : 0,
            transition: 'opacity 0.6s ease',
          }}
        />
      ) : null}
      {/* Scrim above the video / poster (still below content thanks to the DOM
          order within the wrapper's stacking context) to keep text legible. */}
      {overlay ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            background: overlay,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </>
  );
}

export default BackgroundVideo;
